import { ApplicationRef, DestroyRef, EventEmitter, Injectable } from '@angular/core';
import Venue from '../../models/Venue';
import Order from '../../models/Order';
import Payment from '../../models/Payment';
import Customer from '../../models/Customer';
import Address from '../../models/Address';
import { Api } from '../../api/api';
import { PreorderType } from '../../enums/PreorderType';
import { OrderType } from '../../enums/OrderType';
import Preorder from '../../models/Preorder';
import OptionGroup from '../../models/OptionGroup';
import Article from '../../models/Article';
import { getDeinstagCount, OrderUtils } from '../../utils/order-utils';
import { TimeUtils } from '../../utils/time-utils';
import { AlertController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { AuthLoginResponse } from '../../models/AuthLoginResponse';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, interval, Subject, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../environments/environment';
import { CustomerAuth } from '../../models/CustomerAuth';
import { DisplayIdentifier } from '../../enums/DisplayIdentifier';
import {
	axiosErrorToMessage,
	defaultsToArticleOption,
	isActive,
	numberComparator,
	sanitizeId,
	venueAcceptsOrders,
} from '../../utils/utils';
import { DisplayMode } from '../../enums/DisplayMode';
import Table from 'src/smoothr-web-app-core/models/Table';
import { OrderStatus } from 'src/smoothr-web-app-core/enums/OrderStatus';
import { NavigationService } from 'src/app/services/navigation.service';
import CheckoutRecommendation from 'src/smoothr-web-app-core/models/CheckoutRecommendation';
import PromoCode from 'src/smoothr-web-app-core/models/PromoCode';
import { CNCDiscount, SmoothrCNCDiscount } from '../../models/CNCDiscount';
import ArticleCategory from '../../models/ArticleCategory';
import { ArticleCategoryType } from '../../enums/ArticleCategoryType';
import { ChecksResponse } from '../../models/ChecksResponse';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SmoothrSocketService } from '../smoothr-socket/smoothr-socket.service';
import { TerminalTableRequestType } from '../../models/TableRequest';
import { isDoubleMonday } from '../../../app/shared';
import ArticleGroup from '../../models/ArticleGroup';

dayjs.extend(isSameOrAfter);

const localStorageOrderKey = 'repository-service-order';
const localStorageVerifiedOrderKey = 'repository-service-verified-order';
const localStorageCustomerKey = 'repository-service-customer-auth';
const localStoragePaymentKey = 'repository-service-payment';
const localStorageAddressKey = 'repository-service-address';
const localStoragePreviousPreorderKey = 'repository-service-previous-preorder';
const localStorageAuthLoginKey = 'repository-service-auth-login';

@Injectable({
	providedIn: 'root',
})
export class RepositoryService {
	venue = new EventEmitter<Venue>();
	address = new EventEmitter<Address>();
	verifiedOrder = new EventEmitter<Order>();
	payment = new EventEmitter<Payment>();
	customerAuth = new EventEmitter<CustomerAuth>();
	previousPreorder = new EventEmitter<Preorder>();
	authLogin = new EventEmitter<AuthLoginResponse>();
	availableDiscounts$ = new BehaviorSubject<SmoothrCNCDiscount[]>([]);

	recommendationList$ = new BehaviorSubject<CheckoutRecommendation[]>([]);
	_venue: Venue;
	_address: Address;
	_order: Order;
	_verifiedOrder: Order;
	_payment: Payment;
	_customerAuth: CustomerAuth;
	_previousPreorder: Preorder;
	_authLogin: AuthLoginResponse;
	private order = new Subject<Order>();
	observableOrder: Subject<Order> = this.order;
	private venuePromise: Promise<Venue> = null;
	private venuePromiseRunning: string = null;

	table = new BehaviorSubject<string | null>(null);

	private deinstagBlockedAlert: HTMLIonAlertElement;
	private deinstagBlockTimeout: ReturnType<typeof setTimeout>;

	constructor(
		private appRef: ApplicationRef,
		private snackBarCtrl: MatSnackBar,
		private translate: TranslateService,
		private navService: NavigationService,
		private modalCtrl: ModalController,
		private alertCtrl: AlertController,
		private destroyRef: DestroyRef,
		private socket: SmoothrSocketService
	) {
		this._venue = null;
		try {
			this._order = JSON.parse(localStorage.getItem(localStorageOrderKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStorageOrderKey, null);
		}
		try {
			this._payment = JSON.parse(localStorage.getItem(localStoragePaymentKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStoragePaymentKey, null);
		}
		try {
			this._customerAuth = JSON.parse(localStorage.getItem(localStorageCustomerKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStorageCustomerKey, null);
		}
		try {
			this._address = JSON.parse(localStorage.getItem(localStorageAddressKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStorageAddressKey, null);
		}
		try {
			this._verifiedOrder = JSON.parse(localStorage.getItem(localStorageVerifiedOrderKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStorageVerifiedOrderKey, null);
		}
		try {
			this._previousPreorder = JSON.parse(localStorage.getItem(localStoragePreviousPreorderKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStoragePreviousPreorderKey, null);
		}
		try {
			this._authLogin = JSON.parse(localStorage.getItem(localStorageAuthLoginKey));
		} catch (e) {
			console.error(e);
			localStorage.setItem(localStorageAuthLoginKey, null);
		}
		this.order.subscribe(order => {
			if (order) {
				OrderUtils.injectRequiredArticles(this._venue, order);
				OrderUtils.injectDeliveryFees(this._venue, order);
				this.table.next(order.table);

				order.orderedArticles = order.orderedArticles.map(articleGroup => {
					articleGroup.groups = articleGroup.groups.sort((a, b) => numberComparator(a.article.sortOrder, b.article.sortOrder));
					return articleGroup;
				});

				if (this._customerAuth.customer.uid) {
					order.userUid = this._customerAuth?.customer?.uid;
				}
				if (order && !this._customerAuth?.customer?.isAnonymous && this._customerAuth?.customer?.piggyPoints > 0) {
					this._customerAuth.customer.usedPiggyPoints = structuredClone(order.orderedArticles)
						.filter(it => it?.redeemPointsWithPiggy > 0)
						.reduce((a, b) => {
							const sumOfPoints = b?.redeemPointsWithPiggy * b?.quantity;
							return a + sumOfPoints;
						}, 0);
					console.log(this._customerAuth.customer.usedPiggyPoints);
					this.customerAuth.next(this._customerAuth);
				}
			}
			this._order = order;

			// localStorage.setItem(localStorageOrderKey, JSON.stringify(order));
			localStorage.setItem(localStorageOrderKey, JSON.stringify(order));
			appRef.tick();
		});

		this.verifiedOrder.subscribe(verifiedOrder => {
			this._verifiedOrder = verifiedOrder;
			localStorage.setItem(localStorageVerifiedOrderKey, JSON.stringify(verifiedOrder));
			appRef.tick();
		});
		this.address.subscribe(address => {
			this._address = address;
			localStorage.setItem(localStorageAddressKey, JSON.stringify(address));
			appRef.tick();
		});
		this.payment.subscribe(payment => {
			this._payment = payment;
			localStorage.setItem(localStoragePaymentKey, JSON.stringify(payment));
			appRef.tick();
		});
		this.customerAuth.subscribe(async customerAuth => {
			console.log('customer auth', customerAuth);
			if (!customerAuth) {
				console.log('customer logged out');

				// customer was online and now was logged out.
				if (this._customerAuth && this._customerAuth.customer && !this._customerAuth.customer.isAnonymous) {
					this.snackBarCtrl.open(this.translate.instant('repository_service.login_timed_out'), null, {
						duration: 3000,
					});
				}
				try {
					const response = (await Api.signInAnonymous(customerAuth?.customer?.uid)).data;
					this.customerAuth.emit(response);
				} catch (e) {
					console.error(e);
					this.snackBarCtrl.open(axiosErrorToMessage(this.translate, e), null, {
						duration: 2000,
					});
					localStorage.setItem(localStorageCustomerKey, JSON.stringify(null));
				}
				return;
			}

			if (!customerAuth.customer.country) {
				customerAuth.customer.country = environment.countryList[0];
			} else {
				customerAuth.customer.country = customerAuth.customer.country.toLowerCase();
			}

			this._customerAuth = customerAuth;

			Api.authToken = this._customerAuth.token;
			Api.refreshToken = this._customerAuth.refreshToken;
			localStorage.setItem(localStorageCustomerKey, JSON.stringify(customerAuth));
			// if (this._order) {
			// 	this._order.userUid = this._customerAuth.customer.uid;
			// 	this.order.next(this._order);
			// }
			appRef.tick();
		});
		Api.onTokenRefreshed.subscribe(customerAuth => {
			if (!customerAuth) {
				this.customerAuth.emit(null);
				return;
			}
			const merged: CustomerAuth = {
				...this._customerAuth,
				...customerAuth,
				customer: customerAuth.customer ?? this._customerAuth?.customer,
			};
			this.customerAuth.emit(merged);
		});
		this.venue.subscribe(async venue => {
			this._venue = venue;
			this.order.next(this._order);
			if (!this._customerAuth.customer.isAnonymous) {
				this.customerAuth.next(this._customerAuth);
			}
			appRef.tick();
		});
		combineLatest([this.venue, this.order])
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				debounceTime(1_000),
				distinctUntilChanged((a, b) => a[0]?._id === b[0]?._id && a[1]?.table === b[1]?.table)
			)
			.subscribe(async ([venue, order]) => {
				const ids = [venue?._id, venue?.readableId, order?.table].filter(it => it && it.length > 0);
				try {
					await socket.authenticate(ids);
				} catch (e) {
					console.error('Error while authenticating', e);
				}
			});
		this.previousPreorder.subscribe(preorder => {
			this._previousPreorder = preorder;
			localStorage.setItem(localStoragePreviousPreorderKey, JSON.stringify(preorder));
		});
		this.authLogin.subscribe(authLogin => {
			this._authLogin = authLogin;
			localStorage.setItem(localStorageAuthLoginKey, JSON.stringify(authLogin));
		});

		this.address.emit(this._address);
		this.order.next(this._order);
		this.verifiedOrder.emit(this._verifiedOrder);
		this.payment.emit(this._payment);
		this.customerAuth.emit(this._customerAuth);
		this.authLogin.emit(this._authLogin);
	}

	get customer(): Customer {
		return this._customerAuth && this._customerAuth.customer;
	}

	resetVenue() {
		this._venue = null;
		this.venue.emit(this._venue);
	}

	onOrderChange(order: Order) {
		// if (order && this._verifiedOrder && !this._verifiedOrder.isPayed) {
		// 	this.invalidateOrder(this._verifiedOrder);
		// 	this.verifiedOrder.emit(null);
		// }
		this.order.next(order);
	}

	getVenue(forcedId: string | null = null): Promise<Venue> {
		let venueId = forcedId;
		if (venueId === null) {
			venueId = this.getStoredVenueId();
		}
		if (venueId === null || venueId.length <= 0) {
			if (this._venue) {
				return new Promise<Venue>(resolve => resolve(this._venue));
			} else {
				return new Promise<Venue>((resolve, reject) => reject('No Venue ID Provided'));
			}
		}
		if (this._venue && venueId === this._venue._id) {
			this.venue.emit(this._venue);
			return new Promise<Venue>(resolve => resolve(this._venue));
		}
		if (this.venuePromiseRunning === venueId) {
			return this.venuePromise;
		}
		this.venuePromiseRunning = venueId;
		this.venuePromise = new Promise<Venue>(async (resolve, reject) => {
			try {
				const venue = (await Api.getLazyVenue(venueId)).data;
				const discountsToday: CNCDiscount[] = [];
				try {
					if (environment.production) {
						discountsToday.push(...(await Api.getCNCDiscountsToday(venue._id)).filter(it => it.enabled && it.items.length > 0));
					} else {
						discountsToday.push(...(await Api.getCNCDiscounts(venue._id)).filter(it => it.enabled && it.items.length > 0));
					}
				} catch (e) {
					console.log('Cannot load discounts');
				}
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours, venue?.specialOpeningHours ?? []);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				venue.articleCategories = (await Api.getCategoriesWithArticles(venue._id, true)).data;
				venue.panicEndAt = venue.panicEndAt && dayjs(venue.panicEndAt);
				venue.panicEndAt = venue.panicEndAt || dayjs().add(-1, 'day');
				const optionGroups: OptionGroup[] = (await Api.getOptionGroupsByVenue(venue._id)).data.map(og => {
					og.displayMode = og.displayModeMobile ?? og.displayMode;
					if (og.displayMode === DisplayMode.DEFAULT) {
						og.displayMode = DisplayMode.CUSTOM;
					}
					return og;
				});
				const allArticles: Article[] = [];
				venue.articleCategories.forEach(cat => {
					allArticles.push(...cat.articles);
				});
				const smoothrCncDiscounts: SmoothrCNCDiscount[] = discountsToday
					.filter(
						it =>
							(it.enabled && it.objectNum === environment.doubleMondayId) ||
							it.objectNum === environment.studentsId ||
							it.objectNum === environment.deinstagId ||
							!!it.consumerDisplayname?.['en-US']?.trim()
					)
					.map(it => {
						return {
							name: it.name['en-US'],
							venueName: it.hierUnitName['en-US'],
							articles: it.items
								.map(item => {
									return structuredClone(allArticles.find(article => article.number === item.sku));
								})
								.filter(article => !!article),
							enabled: it.enabled,
							id: it.objectNum,
							consumerDisplayname: it.consumerDisplayname?.['en-US']?.trim() || null,
						};
					});
				const generatedPromoCategories: ArticleCategory[] = smoothrCncDiscounts
					.filter(it => it.consumerDisplayname)
					.map(discount => ({
						_id: discount.id.toFixed(0),
						mainCategory: ArticleCategoryType.PROMOS,
						name: {
							de: discount.consumerDisplayname,
							en: discount.consumerDisplayname,
						},
						description: {
							de: '',
							en: '',
						},
						articles: discount.articles,
						visible: discount.enabled,
						hidden: !discount.enabled,
						features: {
							new: false,
							special: true,
							topSelling: false,
							_id: '',
							updatedAt: dayjs().toISOString(),
							createdAt: dayjs().toISOString(),
						},
					}))
					.filter(it => it.articles.length != 0);
				console.log('generatedPromoCategories', generatedPromoCategories);
				this.availableDiscounts$.next(smoothrCncDiscounts);
				venue.articleCategories.push(...generatedPromoCategories);
				venue.articleCategories = venue.articleCategories
					.map(category => {
						// filter inactive categories
						category.articles = category.articles
							.map(article => {
								// filter inactive groups (empty options)
								article.groups = article.groups
									.map(groupId => {
										// @ts-ignore
										const group = optionGroups.find(grp => grp._id === groupId);
										// filter inactive options
										if (!group) {
											return null;
										}
										group.articles = group.articles
											.map(optionArticle => this.sanitizeArticle(venue, optionArticle))
											.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
										if (group?.tags?.length > 0) {
											group.tags = group.tags
												.filter(tag => tag !== undefined && tag !== null)
												.map(tag => {
													if (tag._id === undefined) {
														// @ts-ignore
														return venue.tags.find(srcTag => tag === srcTag._id);
													}
													return tag;
												});
										}
										return group;
									})
									.filter(it => !!it)
									.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder))
									.filter(group => group.articles.length > 0);
								article = this.sanitizeArticle(venue, article);
								return article;
							})
							.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
						return category;
					})
					.map(category => {
						if (!category.sanitizedHours) {
							category.availableHours = TimeUtils.sanitizeHours(category.availableHours);
							category.sanitizedHours = true;
						}
						category.hidden = !(
							category.visible &&
							category.articles.length > 0 &&
							(!category.availableHours ||
								category.availableHours.length === 0 ||
								TimeUtils.doesHoursMatchNow(category.availableHours))
						);

						// console.log(`${category.name.de} is hidden ${category.hidden}`)
						return category;
					})
					.sort((a, b) => numberComparator(a.sortOrder, b.sortOrder));
				this._venue = venue;
				this.venue.emit(this._venue);
				resolve(this._venue);
			} catch (e) {
				this.availableDiscounts$.next([]);
				this.venue.emit(null);
				this.venue.error(e);
				reject(e);
			}
		}).finally(() => {
			this.venuePromiseRunning = null;
		});
		return this.venuePromise;
	}

	async getTable(venueId: string, tableNumber: string): Promise<Table> {
		return (await Api.getTable(venueId, tableNumber)).data;
	}
	async sendDeinstag(
		venue: Venue,
		address: Address,
		deinstagsPromoCode: PromoCode,
		orderType: OrderType = OrderType.STANDARD,
		preorderType: PreorderType,
		guestCount: number,
		table?: Table
	) {
		console.log('sendDeinstag', table);

		const order = new Order();
		order.orderedArticles = [];
		order._id = undefined;
		order.venue = venue._id;
		order.currency = venue.currency;
		order.type = orderType;
		order.promoCodes = [];
		order.giftCard = null;
		if (order.type === OrderType.PREORDER) {
			order.preorder = new Preorder();
			order.preorder.number = '';
			order.preorder.type = preorderType;
			order.preorder.personCount = 1;
			order.table = venue.preorderTable;
			order.tableNumber = 'preorder_table';
			if (preorderType === PreorderType.TAKE_AWAY || preorderType === PreorderType.INSIDE) {
				order.preorder.street = venue.street;
				order.preorder.city = venue.city.de;
				order.preorder.number = venue.number;
				order.preorder.postalCode = venue.postalCode;
				order.preorder.country = venue.country;
				order.preorder.lat = venue.location.coordinates[1];
				order.preorder.lng = venue.location.coordinates[0];
			} else if (address) {
				order.preorder.street = address.street;
				order.preorder.city = address.city;
				order.preorder.number = address.number;
				order.preorder.postalCode = address.postalCode;
				order.preorder.country = address.country;
				order.preorder.lat = address.lat;
				order.preorder.lng = address.lng;
			}
		} else if (order.type === OrderType.CATERING) {
			order.preorder = new Preorder();
			order.preorder.street = venue.street;
			order.preorder.city = venue.city.de;
			order.preorder.number = venue.number;
			order.preorder.postalCode = venue.postalCode;
			order.preorder.country = venue.country;
			order.preorder.lat = venue.location.coordinates[1];
			order.preorder.lng = venue.location.coordinates[0];
		} else if (order.type === OrderType.STANDARD) {
			order.table = table?._id;
			order.tableNumber = table?.number;
			order.preorder = new Preorder();
		}
		const deinstagsOrder = await this.applyDeinstag(order, deinstagsPromoCode, guestCount);
		await this.sendOrder(deinstagsOrder, null, null, true);
	}

	async createOrder(
		venue: Venue,
		address: Address,
		orderType: OrderType = OrderType.STANDARD,
		preorderType: PreorderType,
		table?: Table
	) {
		console.log('createOrder', table);
		const order = new Order();
		order.orderedArticles = [];
		order._id = undefined;
		order.venue = venue._id;
		order.currency = venue.currency;
		order.type = orderType;
		order.promoCodes = [];
		order.giftCard = null;
		if (order.type === OrderType.PREORDER) {
			order.preorder = new Preorder();
			order.preorder.number = '';
			order.preorder.type = preorderType;
			order.preorder.personCount = 1;
			order.table = venue.preorderTable;
			order.tableNumber = 'preorder_table';
			if (preorderType === PreorderType.TAKE_AWAY || preorderType === PreorderType.INSIDE) {
				order.preorder.street = venue.street;
				order.preorder.city = venue.city.de;
				order.preorder.number = venue.number;
				order.preorder.postalCode = venue.postalCode;
				order.preorder.country = venue.country;
				order.preorder.lat = venue.location.coordinates[1];
				order.preorder.lng = venue.location.coordinates[0];
			} else if (address) {
				order.preorder.street = address.street;
				order.preorder.city = address.city;
				order.preorder.number = address.number;
				order.preorder.postalCode = address.postalCode;
				order.preorder.country = address.country;
				order.preorder.lat = address.lat;
				order.preorder.lng = address.lng;
			}
		} else if (order.type === OrderType.CATERING) {
			order.preorder = new Preorder();
			order.preorder.street = venue.street;
			order.preorder.city = venue.city.de;
			order.preorder.number = venue.number;
			order.preorder.postalCode = venue.postalCode;
			order.preorder.country = venue.country;
			order.preorder.lat = venue.location.coordinates[1];
			order.preorder.lng = venue.location.coordinates[0];
		} else if (order.type === OrderType.STANDARD) {
			order.table = table?._id;
			order.tableNumber = table?.number;
			order.preorder = new Preorder();
		}
		this.address.emit(address);
		this.order.next(order);
	}

	async sendOrder(order: Order, authToken: string, customer?: Customer, keepPreorder = false): Promise<void> {
		order.userUid = customer?.uid;

		return new Promise<void>(async (resolve, reject) => {
			try {
				const orderCopy: Order = structuredClone(order);
				if (keepPreorder) {
					orderCopy.type = OrderType.STANDARD;
				}
				let orderResponse: Order;
				if (order._id) {
					orderResponse = (await Api.updateOrder(orderCopy))?.data;
				} else {
					orderResponse = (await Api.createOrder(orderCopy, keepPreorder))?.data;
				}
				this.verifiedOrder.emit(orderResponse);

				resolve();
			} catch (e) {
				reject(e);
			}
		});
	}

	getStoredVenueId(): string {
		return this._order ? this._order.venue : null;
	}

	async reorder(translate: TranslateService, order: Order) {
		const anyOrder = structuredClone(order);
		anyOrder.totalPrice = undefined;
		anyOrder.orderedArticles = anyOrder.orderedArticles.map(oa => {
			oa.totalPrice = undefined;
			return oa;
		});
		const localOrder: Order = anyOrder;
		localOrder.status = undefined;
		const venue = await this.getVenue(localOrder.venue);
		if (
			OrderUtils.isDelivery(localOrder) &&
			venue.deliveryEnabled &&
			venue.deliveryPostalCodes &&
			venue.deliveryPostalCodes.length > 0
		) {
			venue.isPostalDelivery = true;
		}
		if (!venueAcceptsOrders(venue, localOrder.preorder.type)) {
			throw {
				message: translate.instant('errors.reorder_venue_not_open'),
			};
		}
		const articleList = [];
		for (const category of venue.articleCategories) {
			for (const article of category.articles) {
				articleList.push(article);
				for (const group of article.groups) {
					for (const optionArticle of group.articles) {
						articleList.push(optionArticle);
					}
				}
			}
		}
		localOrder.orderedArticles = localOrder.orderedArticles
			.filter(it => !it.isPromo || !it.freeArticle)

			.map(oa => {
				oa.article = articleList.find(
					article => oa.article._id === article._id && isActive(articleList, article, localOrder.type, localOrder.preorder.type)
				);
				if (!oa.article) {
					return null;
				}
				oa.groups = oa.groups
					.map(ao => {
						ao.article = articleList.find(
							article =>
								ao.article._id === article._id && isActive(articleList, article, localOrder.type, localOrder.preorder.type)
						);
						if (!ao.article) {
							return null;
						}
						return ao;
					})
					.filter(ao => !!ao);
				return oa;
			})
			.filter(oa => !!oa);
		localOrder.orderAt = null;
		localOrder._id = null;
		localOrder.promoCodes = [];
		this.order.next(localOrder);
	}

	async getVenuesByAddress(customerGroup: string, address: Address): Promise<Venue[]> {
		const venues: Venue[] = [];
		if (address.postalCode) {
			venues.push(
				...(await this.getVenues(customerGroup, null, null, address.postalCode))
					.map(ven => {
						ven.isPostalDelivery = true;
						return ven;
					})
					.filter(ven => {
						return venueAcceptsOrders(ven, PreorderType.DELIVERY);
					})
			);
		}
		const ids = venues.map(ven => ven._id);
		(await this.getVenues(customerGroup, address.lat, address.lng, null))
			.filter(ven => {
				return venueAcceptsOrders(ven, PreorderType.DELIVERY) || venueAcceptsOrders(ven, PreorderType.TAKE_AWAY);
			})
			.forEach(ven => {
				if (ids.indexOf(ven._id) < 0) {
					venues.push(ven);
				}
			});
		return venues;
	}

	async invalidateOrder(order: Order) {
		// try {
		// 	if (order?._id) {
		// 		const result = await Api.invalidateOrder(order?._id);
		// 		order.sanifairVouchers = [];
		// 	}
		// } catch (e) {
		// 	console.log('Error while updating');
		// }
	}

	async extendOrderWithSanifair(order: Order) {
		// this.stopTimer();
		try {
			// if (order?._id && order.status === OrderStatus.CREATED) {
			// 	await Api.extendSanifairVoucher(order?._id);
			// 	this.startTimer();
			// }
			return order;
		} catch (e) {
			// this.startTimer();
		}
	}

	async getVenues(customerGroup: string, lat: number | null, lng: number | null, postalCode: string | null): Promise<Venue[]> {
		const response = await (postalCode
			? Api.getVenuesBy(postalCode, customerGroup)
			: Api.getVenues(customerGroup, lat, lng, 50, false));
		return response.data
			.map(venue => {
				venue.openingHours = TimeUtils.sanitizeHours(venue.openingHours, venue?.specialOpeningHours ?? []);
				venue.deliveryHours = TimeUtils.sanitizeHours(venue.deliveryHours);
				venue.isPostalDelivery = !!postalCode;
				if (venue.deliveryByRadius === undefined) {
					venue.deliveryByRadius = false;
				}
				return venue;
			})
			.filter(async venue => {
				try {
					return !(venue.panicEndAt && dayjs(venue.panicEndAt).isSameOrAfter(dayjs()));
				} catch (e) {
					console.log(`Venue: ${venue.name} closed because of error`, e);
					return false;
				}
			});
	}

	async cleanRepository() {
		this.resetVenue();
		this.address.emit(null);
		this.order.next(null);
		this.verifiedOrder.emit(null);
		this.payment.emit(null);
	}

	async sendOrderWithoutNavToPayment(order: Order, loading: (loading: boolean) => void) {
		this._order._id = undefined;
		loading(true);
		try {
			this._order.status = OrderStatus.CREATED;
			await this.sendOrder(order, this._authLogin ? this._authLogin.token : null, this._customerAuth.customer);
			loading(false);
		} catch (e) {
			loading(false);
			throw e;
		}
	}

	private sanitizeArticle(venue: Venue, article: Article): Article {
		if (!article) {
			return null;
		}
		if (article.name.de.startsWith('***') || article.name.en.startsWith('***')) {
			article.visible = false;
		}
		if (article && article.groups) {
			article.isFromPrice =
				article.groups
					.map(grp => grp.displayIdentifiers && grp.displayIdentifiers.indexOf(DisplayIdentifier.fullPrice) >= 0)
					.indexOf(true) >= 0;
		}
		if (!article.sanitizedHours) {
			article.availableHours = TimeUtils.sanitizeHours(article.availableHours);
			article.sanitizedHours = true;
		}

		article.hidden = !(
			article.visible &&
			article.isActive &&
			(!article.availableHours || article.availableHours.length === 0 || TimeUtils.doesHoursMatchNow(article.availableHours))
		);

		if (article.displayPrice) {
			article.price = article.displayPrice ? article.displayPrice : article.price;
		}
		// if (article.displayPriceByType) {
		// 	const byType = article.displayPriceByType;
		// 	if (byType.standard) {
		// 		article.priceByType.standard = byType.standard;
		// 	}
		// 	if (byType.preorder.delivery) {
		// 		article.priceByType.preorder.delivery = byType.preorder.delivery;
		// 	}
		// 	if (byType.preorder.inside) {
		// 		article.priceByType.preorder.inside = byType.preorder.inside;
		// 	}
		// 	if (byType.preorder.takeAway) {
		// 		article.priceByType.preorder.takeAway = byType.preorder.takeAway;
		// 	}
		// 	if (byType.terminal.inside) {
		// 		article.priceByType.terminal.inside = byType.terminal.inside;
		// 	}
		// 	if (byType.terminal.takeAway) {
		// 		article.priceByType.terminal.takeAway = byType.terminal.takeAway;
		// 	}
		// }
		if (article?.constrains?.length > 0 && venue?.constrains?.length > 0 && article.visible && article.isActive) {
			const collectionOfConstrains = new Map(venue.constrains.map(it => [it._id, it.isActive]));
			article.isActive = !!article.constrains.find(it => collectionOfConstrains.has(it) && collectionOfConstrains.get(it));
		}
		if (!article.tags) {
			article.tags = [];
		}
		article.tags = article.tags
			.filter(tag => tag !== undefined && tag !== null)
			.map(tag => {
				if (tag._id === undefined) {
					// @ts-ignore
					return venue.tags.find(srcTag => tag === srcTag._id);
				}
				return tag;
			});
		return article;
	}

	findDeinstagProduct(): Article | null {
		if (!this._venue) return null;
		for (const cat of this._venue.articleCategories) {
			for (const article of cat.articles) {
				if (article.tags.find(tag => tag.identifier === 'deinstag')) {
					return article;
				}
			}
		}
		return null;
	}

	async getDeinstagPromoCode(): Promise<PromoCode | null> {
		try {
			const promoCode = (await Api.getPromoCode({ code: environment.deinstagCode, venue: this._venue._id })).data;
			promoCode.availableHours = TimeUtils.sanitizeHours(promoCode.availableHours);
			if (promoCode.availableHours.length && !TimeUtils.doesHoursMatchNow(promoCode.availableHours)) {
				return null;
			}

			return promoCode;
		} catch (e) {
			return null;
		}
	}
	async getDoubleMondayPromoCode(): Promise<PromoCode | null> {
		try {
			return (await Api.getPromoCode({ code: environment.doubleMondayCode, venue: this._venue._id })).data;
		} catch (e) {
			return null;
		}
	}
	doubleMondayEnabled(): boolean {
		return !!this.availableDiscounts$.value.find(it => it.id === environment.doubleMondayId);
	}
	deinstagEnabled(): boolean {
		return !!this.availableDiscounts$.value.find(it => it.id === environment.deinstagId);
	}
	studentsEnabled(): boolean {
		return !!this.availableDiscounts$.value.find(it => it.id === environment.studentsId);
	}

	async applyStudentsThursday() {
		console.log('applyStudentsThursday()');
		try {
			const promoCode = (
				await Api.getPromoCode({
					code: environment.studentsCode,
					venue: this._venue._id,
				})
			).data;
			console.log(promoCode);
			this.onOrderChange(OrderUtils.applyPromo(this.translate, this._venue, this._order, promoCode));
		} catch (e) {
			this.snackBarCtrl.open('Error while applying students discount!');
			console.error(e);
		}
	}

	async applyDeinstag(order: Order, deinstagsPromoCode: PromoCode, guestCount: number): Promise<Order> {
		const deinstagProduct = this.findDeinstagProduct();
		if (!deinstagProduct) {
			return null;
		}
		const deinstagPromoCodeApplied = !!order.promoCodes.find(it => it.code === environment.deinstagCode);
		if (!deinstagPromoCodeApplied) {
			order.promoCodes.push(deinstagsPromoCode);
		}
		const deinstagsArticleGroup = new ArticleGroup();
		deinstagsArticleGroup.article = deinstagProduct;
		deinstagsArticleGroup.quantity = guestCount;
		deinstagsArticleGroup.groups = defaultsToArticleOption(
			deinstagProduct,
			[],
			deinstagProduct.defaults,
			order?.type,
			order.preorder.type
		);
		order.orderedArticles = [deinstagsArticleGroup];
		return order;
	}

	async getChecksOfTable(venue: string, table: string): Promise<ChecksResponse> {
		return Api.getChecksOfTable(sanitizeId(venue), table);
	}

	async addDoubleMonday() {
		const hasDoubleMonday = this._order.promoCodes.some(promoCode => promoCode.code === environment.doubleMondayCode);
		if (!hasDoubleMonday) {
			const doubleMondayPromoCode = await this.getDoubleMondayPromoCode();
			if (doubleMondayPromoCode) {
				const order = structuredClone(this._order);
				order.promoCodes.push(doubleMondayPromoCode);
				order.orderedArticles = order.orderedArticles.map(it => {
					it.isDoubleMonday = isDoubleMonday(this, it.article);
					return it;
				});
				this.onOrderChange(order);
			}
			return;
		}
		this.onOrderChange(this._order);
	}
}
